/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'

// Chakra Components
import {Box, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DynamicImage from '../dynamic-image'
import {formattedURL} from '../../utils/url'
import useTagManager from '../../hooks/use-tag-manager'
const LARGE = 'large'

/**
 * The image gallery displays a hero image and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
const ImageGalleryCompact = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    isProductPartOfSet = false,
    size,
    productIndex,
    slugUrl = '',
    lazy = false,
    styleVariant = 'default',
    product,
    onTileClick = () => {}
}) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const tagManager = useTagManager()
    const variant = styleVariant
    const styles = useMultiStyleConfig('ImageGallery', {size, variant})
    const location = useLocation()
    const productUrl = slugUrl
    const productUrlFormatted = formattedURL(productUrl)

    // Get the 'hero' image for the current variation.
    const heroImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: LARGE,
                selectedVariationAttributes
            }),
        [selectedVariationAttributes]
    )

    useEffect(() => {
        // reset the selected index when location search changes
        setSelectedIndex(0)
    }, [location.search])

    // Get a memoized image group used for the thumbnails. We use the `useMemo` hook
    // so we don't have to waste time filtering the image groups each render if the
    // selected variation attributes haven't changed.
    const thumbnailImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: LARGE,
                selectedVariationAttributes
            }),
        [selectedVariationAttributes]
    )

    const heroImage = heroImageGroup?.images?.[selectedIndex]
    const thumbnailImages = thumbnailImageGroup?.images || []
    const loadingStrategy = lazy ? 'lazy' : 'eager'

    const totalImages = thumbnailImages?.length || 0

    return (
        <Flex direction="column">
            {heroImage && isProductPartOfSet && (
                <Box
                    {...productUrlFormatted}
                    {...styles.heroImageGroup}
                    className="img-wrapper img-wrapper-compact"
                    onClick={(e) => {
                        onTileClick(e)
                        const gwpPwpModalContent = document.querySelector('.gwp-drawer-content')
                        if (gwpPwpModalContent) {
                            const meterType = gwpPwpModalContent.getAttribute('data-meter-type')
                            const breadcrumb =
                                product?.c_breadcrumb
                                    ?.map((item) => item?.htmlValue)
                                    ?.reverse()
                                    ?.join('/') || ''
                            tagManager.productClick(
                                `${meterType} Modal`,
                                product,
                                product,
                                productIndex + 1,
                                breadcrumb,
                                null,
                                `${meterType} Modal`,
                                undefined
                            )
                        }
                    }}
                >
                    <DynamicImage
                        background="gray.100"
                        src={`${heroImage.disBaseLink || heroImage.link}`}
                        widths={['154w', '308w', '462w']}
                        imageProps={{
                            ...styles.setImage,
                            alt: heroImage.alt,
                            loading: loadingStrategy
                        }}
                    />
                </Box>
            )}
            {!isProductPartOfSet && (
                <Flex wrap="wrap">
                    {thumbnailImages.map((image, index) => {
                        const isVideo = image.disBaseLink?.includes('.mp4')
                        const isLastOdd = totalImages % 2 !== 0 && index === totalImages - 1

                        return (
                            <Box
                                key={index}
                                width={isVideo || isLastOdd ? '100%' : 'calc(50% - 8px)'}
                            >
                                <DynamicImage
                                    src={image.disBaseLink || image.link}
                                    imageProps={{
                                        alt: image.alt,
                                        loading: loadingStrategy
                                    }}
                                />
                            </Box>
                        )
                    })}
                </Flex>
            )}
        </Flex>
    )
}

ImageGalleryCompact.propTypes = {
    /**
     * The images array to be rendered
     */
    imageGroups: PropTypes.array,
    /**
     * The current selected variation values
     */
    selectedVariationAttributes: PropTypes.object,
    /**
     * Size of the Image gallery, this will be used to determined the max width from styles
     */
    size: PropTypes.string,
    /**
     * Determines whether the image will be lazy loaded or not
     */
    lazy: PropTypes.bool,
    slugUrl: PropTypes.string,
    isProductPartOfSet: PropTypes.bool,
    styleVariant: PropTypes.string,
    product: PropTypes.any,
    productIndex: PropTypes.any,
    onTileClick: PropTypes.func
}

export default ImageGalleryCompact
